import React, { useState, useEffect, useRef, useMemo } from "react";
import { Dialog, Divider, Checkbox, FormControlLabel } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { useTranslation } from "react-i18next";
import filterIcon from "../../images/FilterIcon.svg";
import "./FilterModal.scss";
import SideArrow from "../../images/RightArroow.svg";
import PrimaryButton from "../Manager/PrimaryButton";
import SearchFilterTextField from "../Manager/SearchFilterTextField";
import infoIcon from "../../images/infoIcon.svg";
import variables from "../../styles/base/_universal.scss";

const CONTENT_TYPE_OPTIONS = [
  { id: "oneTimeContent", displayName: "One-time content", value: true },
  { id: "modularContent", displayName: "Modular Content", value: false },
];

function FilterModal({
  setIsFilterModalOpen,
  setSelectedFilters,
  setShowFilterCount,
  brandsData = [],
  countryData = [],
  mlrStatusData = [],
  indicationData = [],
  handleFilter,
  handleMarketChange,
  values,
  setValues,
  handleReset,
  isBanner = false,
}) {
  const { t } = useTranslation();

  const filterKeys = useMemo(() => {
    const keys = ["Product", "Country", "Banner Type"];
    // if (isBanner) {
    //   keys.push("Content Type");
    // }
    keys.push("MLR Status", "Indication");
    return keys;
  }, [isBanner]);

  const [activeFilter, setActiveFilter] = useState("Product");

  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState(false);
  const [localValues, setLocalValues] = useState({ canReset: false });
  const [isSearchBarEmpty, setIsSearchBarEmpty] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);

  const [lastClickedItem, setLastClickedItem] = useState({});
  const [clickOutside, setClickOutside] = useState(false);

  const [localSelectedFilters, setLocalSelectedFilters] = useState({
    Product: [],
    Country: [],
    "Banner Type": [],
    "Content Type": [],
    "MLR Status": [],
    Indication: [],
  });

  // ✅ Build tab-wise item lists (strictly separated)
  const allItemsByTab = useMemo(() => {
    return {
      Product: brandsData.map((i) => i?.displayName).filter(Boolean),
      Country: countryData.map((i) => i?.displayName).filter(Boolean),
      "Banner Type": ["Static", "Animated"],
      "Content Type": CONTENT_TYPE_OPTIONS.map((item) => item.displayName),
      "MLR Status": Array.isArray(mlrStatusData)
        ? mlrStatusData
            .map((i) => i?.displayName || i?.statusLabel)
            .filter(Boolean)
        : [],
      Indication: indicationData.map((i) => i?.label).filter(Boolean),
    };
  }, [brandsData, countryData, mlrStatusData, indicationData]);

  // ✅ Init selections from parent values
  useEffect(() => {
    const initialSelections = {
      Product: Array.isArray(values?.brand)
        ? values.brand.map((i) => i?.displayName).filter(Boolean)
        : [],
      Country: Array.isArray(values?.market)
        ? values.market.map((i) => i?.displayName).filter(Boolean)
        : [],
      "Banner Type": Array.isArray(values?.templateType)
        ? values.templateType.map((i) => i?.displayName).filter(Boolean)
        : [],
      "Content Type": Array.isArray(values?.contentType)
        ? values.contentType.map((i) => i?.displayName).filter(Boolean)
        : [],
      "MLR Status": Array.isArray(values?.mlrStatus)
        ? values.mlrStatus
            .map((i) => i?.displayName || i?.statusLabel)
            .filter(Boolean)
        : [],
      Indication: Array.isArray(values?.indication)
        ? values.indication.map((i) => i?.label).filter(Boolean)
        : [],
    };
    setLocalSelectedFilters(initialSelections);
  }, [values]);

  // ✅ Reset search when switching tabs
  useEffect(() => {
    setSearchText("");
    setSearch(false);
    setLocalValues({ canReset: false });
    setIsSearchBarEmpty(true);
    setHasSearched(false);
  }, [activeFilter]);

  const getTotalSelectedCount = () =>
    Object.values(localSelectedFilters).reduce(
      (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
      0
    );

  // ✅ Filter items only for active tab
  const filteredItems = useMemo(() => {
    const items = allItemsByTab[activeFilter] || [];
    if (!hasSearched || !searchText.trim()) return items;

    const term = searchText.toLowerCase().trim();
    return items.filter((x) => x && x.toLowerCase().includes(term));
  }, [allItemsByTab, activeFilter, hasSearched, searchText]);

  const handleFilterClick = (filterName) => setActiveFilter(filterName);

  const handleCheckboxChange = (filterName, value) => {
    setLocalSelectedFilters((prev) => ({
      ...prev,
      [filterName]: prev[filterName].includes(value)
        ? prev[filterName].filter((x) => x !== value)
        : [...prev[filterName], value],
    }));
  };

  const handleBannerTypeChange = (event) => {
    const value = event.target.value;
    setLocalSelectedFilters((prev) => ({
      ...prev,
      "Banner Type": value ? [value] : [],
    }));
  };

  const clearBannerTypeSelection = () => {
    setLocalSelectedFilters((prev) => ({ ...prev, "Banner Type": [] }));
  };

  const handleApply = () => {
    setIsFilterModalOpen(false);
    setShowFilterCount(getTotalSelectedCount());
    setSelectedFilters(localSelectedFilters);

    const updatedValues = { ...values };

    // Product
    if (localSelectedFilters.Product.length > 0) {
      const selectedProducts = brandsData.filter((b) =>
        localSelectedFilters.Product.includes(b?.displayName)
      );
      updatedValues.brand = selectedProducts;
      handleFilter("brand", selectedProducts);
    } else {
      updatedValues.brand = [];
      handleFilter("brand", []);
    }

    // Country
    if (localSelectedFilters.Country.length > 0) {
      const selectedCountries = countryData.filter((c) =>
        localSelectedFilters.Country.includes(c?.displayName)
      );
      updatedValues.market = selectedCountries;
      handleFilter("market", selectedCountries);
      handleMarketChange(selectedCountries[0] || null);
    } else {
      updatedValues.market = [];
      handleFilter("market", []);
      handleMarketChange(null);
    }

    // Banner Type
    if (localSelectedFilters["Banner Type"].length > 0) {
      const templateTypeObjects = localSelectedFilters["Banner Type"].map(
        (t) => ({
          displayName: t,
        })
      );
      updatedValues.templateType = templateTypeObjects;
      handleFilter("templateType", templateTypeObjects);
    } else {
      updatedValues.templateType = [];
      handleFilter("templateType", []);
    }

    // Content Type
    if (localSelectedFilters["Content Type"].length > 0) {
      const selectedContentType = CONTENT_TYPE_OPTIONS.filter((option) =>
        localSelectedFilters["Content Type"].includes(option.displayName)
      ).map((option) => ({
        id: option.id,
        displayName: option.displayName,
        value: option.value,
        metaDataType: "contentType",
      }));
      updatedValues.contentType = selectedContentType;
      handleFilter("contentType", selectedContentType);
    } else {
      updatedValues.contentType = [];
      handleFilter("contentType", []);
    }

    // MLR Status
    if (localSelectedFilters["MLR Status"].length > 0) {
      const selectedMlr = mlrStatusData.filter((s) => {
        const name = s?.displayName || s?.statusLabel || s?.name || s?.id;
        return localSelectedFilters["MLR Status"].includes(name);
      });
      updatedValues.mlrStatus = selectedMlr;
      handleFilter("mlrStatus", selectedMlr);
    } else {
      updatedValues.mlrStatus = [];
      handleFilter("mlrStatus", []);
    }

    // Indication
    if (localSelectedFilters.Indication.length > 0) {
      const selectedInd = indicationData.filter((i) =>
        localSelectedFilters.Indication.includes(i?.label)
      );
      updatedValues.indication = selectedInd;
      handleFilter("indication", selectedInd);
    } else {
      updatedValues.indication = [];
      handleFilter("indication", []);
    }

    setValues({ ...updatedValues, canReset: true });
  };

  const handleCancel = () => {
    setIsFilterModalOpen(false);

    const original = {
      Product: Array.isArray(values?.brand)
        ? values.brand.map((i) => i?.displayName).filter(Boolean)
        : [],
      Country: Array.isArray(values?.market)
        ? values.market.map((i) => i?.displayName).filter(Boolean)
        : [],
      "Banner Type": Array.isArray(values?.templateType)
        ? values.templateType.map((i) => i?.displayName).filter(Boolean)
        : [],
      "Content Type": Array.isArray(values?.contentType)
        ? values.contentType.map((i) => i?.displayName).filter(Boolean)
        : [],
      "MLR Status": Array.isArray(values?.mlrStatus)
        ? values.mlrStatus
            .map((i) => i?.displayName || i?.statusLabel)
            .filter(Boolean)
        : [],
      Indication: Array.isArray(values?.indication)
        ? values.indication.map((i) => i?.label).filter(Boolean)
        : [],
    };

    setLocalSelectedFilters(original);
  };

  const handleClearAllFilters = () => {
    setLocalSelectedFilters({
      Product: [],
      Country: [],
      "Banner Type": [],
      "Content Type": [],
      "MLR Status": [],
      Indication: [],
    });
    handleReset();
  };

  const handleSearch = () => {
    if (searchText.trim()) {
      setSearch(false);
      setHasSearched(true);
      setLocalValues((prev) => ({
        ...prev,
        templateNameInput: searchText,
        canReset: true,
      }));
    }
  };

  const handleTextChange = (value) => {
    const hasText = value.trim().length > 0;
    setSearchText(value);
    setSearch(hasText);
    setIsSearchBarEmpty(!hasText);
    setHasSearched(hasText);

    if (hasSearched && value !== localValues.templateNameInput) {
      setSearch(hasText);
    }
  };

  const handleClearSearch = () => {
    setSearchText("");
    setSearch(false);
    setLocalValues({ canReset: false });
    setIsSearchBarEmpty(true);
    setHasSearched(false);
  };

  // outside click logic (kept from your old code)
  const wrapperRef = useRef(null);
  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setClickOutside(false);
    }
  };
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleClick = () => setClickOutside(true);

  return (
    <div className="blurryBackground">
      <Dialog
        open={true}
        className="filterDialog"
        maxWidth="md"
        fullWidth
        aria-labelledby="filter-dialog-title">
        <div className="filterDialogHeader">
          <div className="filterHeaderLeft">
            <span className="filterIcon">
              <img src={filterIcon} height={15} width={14} alt="filter" />
            </span>
            <span className="filterTitle">
              Filters ({getTotalSelectedCount()})
            </span>
          </div>

          <div className="filterHeaderRight">
            <div
              onClick={
                getTotalSelectedCount() > 0 ? handleClearAllFilters : undefined
              }
              className={
                getTotalSelectedCount() > 0
                  ? "clearbtnEnable"
                  : "clearbtnDisable"
              }
            >
              Clear all
            </div>
          </div>
        </div>

        <DialogContent className="filterDialogContent">
          <div className="filterContainer">
            {/* Left Panel */}
            <div className="filterLeftPanel">
              {filterKeys.map((key) => (
                <div
                  key={key}
                  className={`filterItem ${
                    activeFilter === key ? "active" : ""
                  }`}
                  onClick={() => handleFilterClick(key)}
                >
                  <span className="filterName">
                    {key}{" "}
                    {localSelectedFilters[key].length > 0
                      ? `(${localSelectedFilters[key].length})`
                      : ""}
                  </span>
                  <img src={SideArrow} className="img-fluid" alt="" />
                </div>
              ))}
            </div>

            {/* Right Panel */}
            <div className="filterRightPanel">
              <div className="filterSearchContainer">
                <div style={{ display: "flex", margin: "0 16px" }}>
                  <SearchFilterTextField
                    style={{ width: "100%", marginLeft: "20px" }}
                    expandable
                    inlineButton
                    search={search}
                    values={localValues}
                    onTextChange={handleTextChange}
                    handleSearch={handleSearch}
                    inputValue={searchText}
                    setSearchText={setSearchText}
                    setValues={setLocalValues}
                    setIsSearchBarEmpty={setIsSearchBarEmpty}
                    isSearchBarEmpty={isSearchBarEmpty}
                    placeholder={"Search by Name"}
                    onClear={handleClearSearch}
                  />
                </div>
              </div>

              {/* Banner Type clear selection */}
              {activeFilter === "Banner Type" && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    margin: "8px 16px",
                  }}
                >
                  <span
                    onClick={
                      localSelectedFilters["Banner Type"].length > 0
                        ? clearBannerTypeSelection
                        : undefined
                    }
                    className={
                      localSelectedFilters["Banner Type"].length > 0
                        ? "clearbtnEnables"
                        : "clearbtnDisables"
                    }
                  >
                    Clear Selection
                  </span>
                </div>
              )}

              {/* ✅ IMPORTANT: key={activeFilter} forces remount so Product rows can never “stick” */}
              <div
                key={activeFilter}
                className="filterValuesContainer"
                ref={wrapperRef}
              >
                {/* Banner Type -> Radios only */}
                {activeFilter === "Banner Type" ? (
                  filteredItems.length > 0 ? (
                    <RadioGroup
                      value={localSelectedFilters["Banner Type"][0] ?? ""}
                      onChange={handleBannerTypeChange}
                    >
                      {filteredItems.map((item) => (
                        <div
                          key={`radio-${activeFilter}-${item}`}
                          className="filterValueItem"
                          onClick={handleClick}
                        >
                          <FormControlLabel
                            value={item}
                            control={<Radio color="primary" size="small" />}
                            label={item}
                            className="filterCheckbox"
                          />
                        </div>
                      ))}
                    </RadioGroup>
                  ) : (
                    <div
                      style={{
                        margin: "0 12px",
                        fontSize: variables.fontSmall,
                        color: variables.grayFilterColor,
                        display: "flex",
                      }}
                    >
                      <img
                        src={infoIcon}
                        alt="Info"
                        style={{
                          marginRight: "6px",
                          marginBottom: "30px",
                          marginTop: "3px",
                        }}
                      />
                      <p>No Result Found, Try Using Different Keywords</p>
                    </div>
                  )
                ) : filteredItems.length > 0 ? (
                  // Other tabs -> checkboxes only
                  (filteredItems.map((item) => {
                    const isLastClicked =
                      lastClickedItem[activeFilter] === item;
                    const isChecked =
                      Array.isArray(localSelectedFilters[activeFilter]) &&
                      localSelectedFilters[activeFilter].includes(item);

                    return (
                      <div
                        key={`chk-${activeFilter}-${item}`}
                        className={
                          isLastClicked && isChecked && clickOutside
                            ? "filterValueItem selectionState"
                            : "filterValueItem"
                        }
                        onClick={handleClick}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={() => {
                                handleCheckboxChange(activeFilter, item);
                                setLastClickedItem((prev) => ({
                                  ...prev,
                                  [activeFilter]: item,
                                }));
                              }}
                              color="primary"
                              size="small"
                            />
                          }
                          label={item}
                          className="filterCheckbox"
                        />
                      </div>
                    );
                  }))
                ) : (
                  <div
                    style={{
                      margin: "14px 16px",
                      fontSize: variables.fontSmall,
                      color: variables.grayFilterColor,
                      display: "flex",
                    }}
                  >
                    <img
                      src={infoIcon}
                      alt="Info"
                      style={{
                        marginRight: "6px",
                        marginBottom: "30px",
                        marginTop: "3px",
                      }}
                    />
                    <p>No Result Found, Try Using Different Keywords</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>

        <Divider className="dialogFooterLine" />

        <DialogActions className="filterDialogActions">
          <div className="footerActionss">
            <div onClick={handleCancel} className="clearbtnEnable">
              CANCEL
            </div>
            <div onClick={handleApply}>
              <PrimaryButton
                title="APPLY"
                disabled={getTotalSelectedCount() === 0}
              />
            </div>
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default FilterModal;
